import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const dashboardLinks = [
  { title: 'Site Theme', href: '/dashboard/site-theme', description: 'Change your website theme, profile blocks, background, and tile feel.', kind: 'standard' },
  { title: 'Pages', href: '/dashboard/pages', description: "Answer today's prompt and collect searchable tiles from your life.", kind: 'pages' },
  { title: 'Problems', href: '/dashboard/problems', description: 'Find help, share solutions, and discuss problem documents with real users.', kind: 'problems' },
  { title: 'Daily', href: '/dashboard/daily', description: 'Complete reflections, habits, games, and routines that become history.', kind: 'standard' },
  { title: 'Content', href: '/dashboard/content', description: 'Write stories, timeline entries, and longer autobiographical material.', kind: 'standard' },
  { title: 'Social', href: '/dashboard/social', description: 'Friend, family, and community signals worth seeing today.', kind: 'social' },
  { title: 'Settings', href: '/dashboard/settings', description: 'Prepare account, privacy category, and future AI preferences.', kind: 'standard' },
];

const socialHighlights = [
  'Mia posted a new family page about a childhood memory.',
  'Jon completed a 10K training milestone.',
  'A friend birthday is coming up this week.',
  'Three friends saved the Daily Reflection component.',
  'A popular page from your circle is about lessons learned the hard way.',
];

const defaultTilePrefs = {
  Pages: { size: 'detailed', color: '#fffaf2', opacity: 90 },
  Social: { size: 'detailed', color: '#fffaf2', opacity: 90 },
};

function DashboardTile({ item, prefs, onPrefsChange }) {
  const [showSettings, setShowSettings] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const tilePrefs = prefs[item.title] || { size: 'normal', color: '#fffaf2', opacity: 86 };

  useEffect(() => {
    if (item.kind !== 'social') return undefined;
    const interval = setInterval(() => {
      setHighlightIndex((current) => (current + 1) % socialHighlights.length);
    }, 5200);
    return () => clearInterval(interval);
  }, [item.kind]);

  function updatePref(field, value) {
    onPrefsChange(item.title, { ...tilePrefs, [field]: value });
  }

  return (
    <article
      className={`surface-card dashboard-tile dashboard-tile-${tilePrefs.size}`}
      style={{ '--tile-bg': tilePrefs.color, '--tile-opacity': Number(tilePrefs.opacity) / 100 }}
    >
      <button className="tile-cog" type="button" aria-label={`${item.title} settings`} onClick={() => setShowSettings((current) => !current)}>...</button>
      <Link className="card-link dashboard-tile-link" to={item.href}>
        <p className="tag">{item.kind === 'pages' ? "Today's page" : item.kind === 'social' ? 'Your circle' : item.kind === 'problems' ? 'Peer support' : 'Workspace'}</p>
        <h2>{item.title}</h2>
        <p>{item.description}</p>
        {item.kind === 'pages' ? (
          <div className="dashboard-detail-card">
            <strong>What ordinary part of today might future you want to remember?</strong>
            <span>Open Pages to answer, post, or choose another page tab.</span>
          </div>
        ) : null}
        {item.kind === 'social' ? (
          <div className="dashboard-detail-card social-highlight" key={highlightIndex}>
            <strong>Highlight</strong>
            <span>{socialHighlights[highlightIndex]}</span>
          </div>
        ) : null}
        {item.kind === 'problems' ? (
          <div className="dashboard-detail-card">
            <strong>Open a solution document</strong>
            <span>Choose anonymity, write key points, and let others comment if it is not private.</span>
          </div>
        ) : null}
      </Link>
      {showSettings ? (
        <div className="tile-settings inline-panel stacked-form">
          <label>Size
            <select value={tilePrefs.size} onChange={(event) => updatePref('size', event.target.value)}>
              <option value="compact">Compact</option>
              <option value="normal">Normal</option>
              <option value="detailed">Detailed</option>
              <option value="large">Large</option>
            </select>
          </label>
          <label>Color<input type="color" value={tilePrefs.color} onChange={(event) => updatePref('color', event.target.value)} /></label>
          <label>Opacity<input type="range" min="55" max="100" value={tilePrefs.opacity} onChange={(event) => updatePref('opacity', event.target.value)} /></label>
        </div>
      ) : null}
    </article>
  );
}

function Dashboard() {
  const [tilePrefs, setTilePrefs] = useState(() => {
    try {
      return { ...defaultTilePrefs, ...JSON.parse(localStorage.getItem('cyberautography:dashboardTilePrefs') || '{}') };
    } catch (_err) {
      return defaultTilePrefs;
    }
  });

  useEffect(() => {
    localStorage.setItem('cyberautography:dashboardTilePrefs', JSON.stringify(tilePrefs));
  }, [tilePrefs]);

  function updateTilePrefs(title, prefs) {
    setTilePrefs((current) => ({ ...current, [title]: prefs }));
  }

  return (
    <>
      <SEO title="Dashboard" />
      <section className="page-heading">
        <p className="eyebrow">Workspace</p>
        <h1>Customize how YOU want to write your life story. It&apos;s your autobiography. In cyberspace.</h1>
      </section>
      <section className="feature-grid hub-grid">
        {dashboardLinks.map((item) => <DashboardTile item={item} prefs={tilePrefs} onPrefsChange={updateTilePrefs} key={item.href} />)}
      </section>
    </>
  );
}

export default Dashboard;
